import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Form,
  Input,
  InputNumber,
  Switch,
  Select,
  AutoComplete,
  Table,
  Tag,
  Tooltip,
  Typography,
  Space,
  Alert,
} from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  ReloadOutlined,
  EyeOutlined,
  ToolOutlined,
} from "@ant-design/icons";
import { Button, Modal, PageHeader } from "../../components/Common";
import { useConfirm } from "../../hooks/useConfirm";
import { useMutations } from "../../hooks/useApiMutation";

const { Text } = Typography;

/**
 * Admin catalog for LLM models. Admins can add new models per provider
 * (e.g. register more Ollama Cloud models), edit, enable/disable and delete them.
 * Backed by /v1/llm-models (GET with includeDisabled, POST, PUT/{id}, DELETE/{id}).
 */
const LlmModelsList = () => {
  const { mutate } = useMutations();
  const mutateRef = useRef(mutate);
  mutateRef.current = mutate;
  const { confirmAction } = useConfirm();

  const [models, setModels] = useState([]);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [providerFilter, setProviderFilter] = useState(null);
  const [search, setSearch] = useState("");

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();

  const loadProviders = useCallback(async () => {
    const { data } = await mutateRef.current("fetchLlmProviders", {
      method: "get",
      endpoint: "/v1/llm-models/providers",
    });
    setProviders(data?.data || []);
  }, []);

  const loadModels = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await mutateRef.current("fetchLlmModels", {
        method: "get",
        endpoint: "/v1/llm-models",
        params: { includeDisabled: true },
      });
      setModels(data?.data || []);
    } catch {
      setModels([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProviders();
    loadModels();
  }, [loadProviders, loadModels]);

  const providerOptions = useMemo(
    () => providers.map((p) => ({ value: p.provider, label: p.provider })),
    [providers]
  );

  const filteredModels = useMemo(() => {
    const term = search.trim().toLowerCase();
    return models.filter((m) => {
      if (providerFilter && m.provider !== providerFilter) return false;
      if (!term) return true;
      return (
        m.modelId?.toLowerCase().includes(term) ||
        m.displayName?.toLowerCase().includes(term) ||
        m.description?.toLowerCase().includes(term)
      );
    });
  }, [models, providerFilter, search]);

  const onCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({
      provider: providerFilter || undefined,
      isEnabled: true,
      supportsVision: false,
      supportsTools: true,
      sortOrder: 100,
      inputPricePerMillion: 0,
      outputPricePerMillion: 0,
    });
    setOpen(true);
  };

  const onEdit = (record) => {
    setEditing(record);
    form.setFieldsValue(record);
    setOpen(true);
  };

  const handleOk = async () => {
    const values = await form.validateFields();
    const payload = {
      provider: values.provider,
      modelId: values.modelId,
      displayName: values.displayName,
      description: values.description || null,
      contextWindow: values.contextWindow ?? null,
      maxOutputTokens: values.maxOutputTokens ?? null,
      supportsVision: !!values.supportsVision,
      supportsTools: !!values.supportsTools,
      isEnabled: !!values.isEnabled,
      sortOrder: values.sortOrder ?? 100,
      inputPricePerMillion: values.inputPricePerMillion ?? 0,
      outputPricePerMillion: values.outputPricePerMillion ?? 0,
    };

    if (editing) {
      await mutateRef.current("updateLlmModel", {
        method: "put",
        endpoint: `/v1/llm-models/${editing.llmModelId}`,
        payload,
        alert: { success: `Model "${payload.modelId}" updated.` },
      });
    } else {
      await mutateRef.current("createLlmModel", {
        method: "post",
        endpoint: "/v1/llm-models",
        payload,
        alert: { success: `Model "${payload.modelId}" added.` },
      });
    }
    setOpen(false);
    loadModels();
    loadProviders();
  };

  const onDelete = (record) =>
    confirmAction({
      key: "deleteLlmModel",
      method: "delete",
      endpoint: `/v1/llm-models/${record.llmModelId}`,
      title: "Delete model",
      content: `Remove "${record.displayName}" (${record.provider} · ${record.modelId}) from the catalog?`,
      okText: "Delete",
      okType: "danger",
      onSuccess: () => {
        loadModels();
        loadProviders();
      },
    });

  const columns = [
    {
      title: "Provider",
      dataIndex: "provider",
      key: "provider",
      render: (v) => <Tag color="blue">{v}</Tag>,
    },
    {
      title: "Model",
      key: "model",
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <Text strong>{r.displayName}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {r.modelId}
          </Text>
        </Space>
      ),
    },
    {
      title: "Context",
      dataIndex: "contextWindow",
      key: "contextWindow",
      render: (v) => (v ? v.toLocaleString() : "—"),
    },
    {
      title: "Max output",
      dataIndex: "maxOutputTokens",
      key: "maxOutputTokens",
      render: (v) => (v ? v.toLocaleString() : "—"),
    },
    {
      title: "Price €/1M (in / out)",
      key: "price",
      render: (_, r) => {
        const input = Number(r.inputPricePerMillion) || 0;
        const output = Number(r.outputPricePerMillion) || 0;
        if (!input && !output) return <Text type="secondary">—</Text>;
        return (
          <Text>
            {input} / {output}
          </Text>
        );
      },
    },
    {
      title: "Capabilities",
      key: "capabilities",
      render: (_, r) => (
        <Space size={4}>
          {r.supportsVision && (
            <Tooltip title="Vision">
              <Tag icon={<EyeOutlined />} color="geekblue">
                Vision
              </Tag>
            </Tooltip>
          )}
          {r.supportsTools && (
            <Tooltip title="Tool calling">
              <Tag icon={<ToolOutlined />} color="purple">
                Tools
              </Tag>
            </Tooltip>
          )}
          {!r.supportsVision && !r.supportsTools && <Text type="secondary">—</Text>}
        </Space>
      ),
    },
    {
      title: "Status",
      dataIndex: "isEnabled",
      key: "isEnabled",
      render: (v) =>
        v ? <Tag color="success">Enabled</Tag> : <Tag color="default">Disabled</Tag>,
    },
    { title: "Order", dataIndex: "sortOrder", key: "sortOrder" },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit">
            <Button variant="ghost" icon={<EditOutlined />} onClick={() => onEdit(record)} />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              variant="ghost"
              danger
              icon={<DeleteOutlined />}
              onClick={() => onDelete(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="LLM models"
        subtitle="Manage the catalog of models available to agents. Add new models per provider, edit their limits, or enable/disable them."
        extra={
          <Button variant="primary" icon={<PlusOutlined />} onClick={onCreate}>
            Add model
          </Button>
        }
      />

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="API keys are configured separately on the LLM Keys screen. A new provider becomes selectable for agents once it has at least one enabled model and a key."
      />

      <Space style={{ marginBottom: 16, flexWrap: "wrap" }}>
        <Select
          allowClear
          placeholder="All providers"
          style={{ width: 200 }}
          value={providerFilter}
          onChange={(v) => setProviderFilter(v || null)}
          options={providerOptions}
        />
        <Input.Search
          placeholder="Search model / description"
          allowClear
          value={search}
          onChange={(e) => setSearch(e.target.value || "")}
          style={{ width: 260 }}
        />
        <Tooltip title="Refresh">
          <Button
            variant="ghost"
            icon={<ReloadOutlined />}
            onClick={() => {
              loadModels();
              loadProviders();
            }}
          />
        </Tooltip>
      </Space>

      <Table
        rowKey="llmModelId"
        columns={columns}
        dataSource={filteredModels}
        loading={loading}
        scroll={{ x: "max-content" }}
        pagination={{ pageSize: 10, showSizeChanger: true }}
      />

      <Modal
        title={editing ? "Edit model" : "Add model"}
        open={open}
        onConfirm={handleOk}
        onClose={() => setOpen(false)}
        confirmText={editing ? "Save" : "Add"}
        width={620}
      >
        <Form form={form} layout="vertical">
          <div style={{ display: "flex", gap: 12 }}>
            <Form.Item
              name="provider"
              label="Provider"
              style={{ flex: 1 }}
              rules={[
                { required: true, message: "Provider is required" },
                { max: 20, message: "Max 20 characters" },
              ]}
            >
              <AutoComplete
                options={providerOptions}
                placeholder="e.g. Ollama"
                filterOption={(input, option) =>
                  (option?.value || "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
            </Form.Item>
            <Form.Item
              name="modelId"
              label="Model id"
              style={{ flex: 1 }}
              rules={[
                { required: true, message: "Model id is required" },
                { max: 100, message: "Max 100 characters" },
              ]}
            >
              <Input placeholder="e.g. gpt-oss:120b" />
            </Form.Item>
          </div>

          <Form.Item
            name="displayName"
            label="Display name"
            rules={[
              { required: true, message: "Display name is required" },
              { max: 150, message: "Max 150 characters" },
            ]}
          >
            <Input placeholder="e.g. GPT-OSS 120B" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
            rules={[{ max: 500, message: "Max 500 characters" }]}
          >
            <Input.TextArea rows={2} placeholder="Short description shown to admins" />
          </Form.Item>

          <div style={{ display: "flex", gap: 12 }}>
            <Form.Item name="contextWindow" label="Context window" style={{ flex: 1 }}>
              <InputNumber min={0} style={{ width: "100%" }} placeholder="tokens" />
            </Form.Item>
            <Form.Item name="maxOutputTokens" label="Max output tokens" style={{ flex: 1 }}>
              <InputNumber min={0} style={{ width: "100%" }} placeholder="tokens" />
            </Form.Item>
            <Form.Item name="sortOrder" label="Sort order" style={{ flex: 1 }}>
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>
          </div>

          <div style={{ display: "flex", gap: 12 }}>
            <Form.Item
              name="inputPricePerMillion"
              label="Input price (€ / 1M tokens)"
              style={{ flex: 1 }}
              tooltip="Real provider cost per 1,000,000 prompt/input tokens, in EUR."
              rules={[{ type: "number", min: 0, message: "Must be 0 or greater" }]}
            >
              <InputNumber min={0} step={0.5} style={{ width: "100%" }} placeholder="e.g. 5" />
            </Form.Item>
            <Form.Item
              name="outputPricePerMillion"
              label="Output price (€ / 1M tokens)"
              style={{ flex: 1 }}
              tooltip="Real provider cost per 1,000,000 completion/output tokens, in EUR."
              rules={[{ type: "number", min: 0, message: "Must be 0 or greater" }]}
            >
              <InputNumber min={0} step={0.5} style={{ width: "100%" }} placeholder="e.g. 25" />
            </Form.Item>
          </div>

          <Space size="large">
            <Form.Item
              name="supportsVision"
              label="Vision"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
            <Form.Item
              name="supportsTools"
              label="Tool calling"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
            <Form.Item name="isEnabled" label="Enabled" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Space>
        </Form>
      </Modal>
    </div>
  );
};

export default LlmModelsList;
